import { createWebhookSchema, type CreatedWebhook } from '@nexo/types';
import { Avatar, Button, EmptyState, IconButton, Input, Spinner, useToast } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bot, Check, Copy, RefreshCw, Trash2, Webhook } from 'lucide-react';
import { useState, type SubmitEvent } from 'react';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { formatRelative } from '../../lib/format';
import { keys, useWebhooks } from '../../lib/queries';
import { can } from '../../lib/permissions';
import { Trans, useTranslation } from 'react-i18next';
import { errorMessage } from '../../lib/forms';

/** Incoming webhooks of the current channel (Discord/Slack compatible). */
export function IntegrationsPanel({ channelId }: { channelId: string | undefined }) {
  const { channels } = useTeam();
  const channel = channelId ? channels.find((c) => c.id === channelId) : undefined;
  const { t } = useTranslation();
  if (!channel || channel.kind !== 'channel') {
    return (
      <EmptyState
        icon={<Webhook className="size-7" />}
        title={t('Integraciones por canal')}
        description={t(
          'Abre un canal para conectar servicios externos (GitHub, CI, alertas…) mediante webhooks.',
        )}
      />
    );
  }
  return <WebhookList channelId={channel.id} channelName={channel.name} />;
}

function WebhookList({ channelId, channelName }: { channelId: string; channelName: string }) {
  const { team } = useTeam();
  const isAdmin = can(team, 'manage_webhooks');
  const webhooks = useWebhooks(channelId);
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState<CreatedWebhook | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: keys.webhooks(channelId) });

  const regenerate = useMutation({
    mutationFn: api.webhooks.regenerate,
    onSuccess: (webhook) => {
      setRevealed(webhook);
      void refresh();
    },
    onError: (err) =>
      toast({ title: t('No se pudo regenerar'), description: errorMessage(err), tone: 'error' }),
  });
  const remove = useMutation({
    mutationFn: api.webhooks.remove,
    onSuccess: () => {
      setRevealed(null);
      void refresh();
    },
    onError: (err) =>
      toast({ title: t('No se pudo eliminar'), description: errorMessage(err), tone: 'error' }),
  });

  return (
    <div className="scrollbar-thin flex-1 overflow-y-auto p-4">
      <p className="mb-4 text-sm text-muted">
        <Trans
          i18nKey="Los webhooks permiten que otros servicios publiquen mensajes en <b>#{{channel}}</b>. Son compatibles con el formato de Discord y con el <code>text</code> de Slack."
          values={{ channel: channelName }}
          components={{ b: <strong className="text-text" />, code: <code className="text-xs" /> }}
        />
      </p>

      {isAdmin && <CreateWebhook channelId={channelId} onCreated={setRevealed} />}
      {revealed && <RevealedUrl webhook={revealed} onDismiss={() => setRevealed(null)} />}

      {webhooks.isPending ? (
        <div className="flex justify-center py-6 text-muted">
          <Spinner />
        </div>
      ) : (webhooks.data ?? []).length === 0 ? (
        <p className="rounded-xl border border-dashed border-border-strong px-4 py-6 text-center text-sm text-subtle">
          {t('Este canal aún no tiene webhooks.')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {webhooks.data?.map((webhook) => (
            <li
              key={webhook.id}
              className="flex items-center gap-3 rounded-xl border border-border bg-surface px-3 py-2.5 shadow-panel"
            >
              {webhook.avatarUrl ? (
                <Avatar id={webhook.id} name={webhook.name} src={webhook.avatarUrl} size="sm" />
              ) : (
                <span className="flex size-7 items-center justify-center rounded-full bg-accent-soft text-accent">
                  <Bot className="size-4" />
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{webhook.name}</span>
                <span className="block truncate text-xs text-muted">
                  {webhook.lastUsedAt
                    ? t('Último uso {{time}}', { time: formatRelative(webhook.lastUsedAt) })
                    : t('Sin usar todavía')}
                  {webhook.createdBy &&
                    t(' · por {{name}}', { name: webhook.createdBy.displayName })}
                </span>
              </span>
              {isAdmin && (
                <>
                  <IconButton
                    label={t('Regenerar URL')}
                    size="sm"
                    onClick={() => regenerate.mutate(webhook.id)}
                  >
                    <RefreshCw />
                  </IconButton>
                  <IconButton
                    label={t('Eliminar webhook')}
                    size="sm"
                    className="hover:text-danger"
                    onClick={() => {
                      if (
                        window.confirm(
                          t(
                            '¿Eliminar el webhook «{{name}}»? Dejará de funcionar inmediatamente.',
                            { name: webhook.name },
                          ),
                        )
                      ) {
                        remove.mutate(webhook.id);
                      }
                    }}
                  >
                    <Trash2 />
                  </IconButton>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {!isAdmin && (
        <p className="mt-4 text-xs text-subtle">
          {t('Solo los administradores del equipo pueden crear webhooks.')}
        </p>
      )}
    </div>
  );
}

function CreateWebhook({
  channelId,
  onCreated,
}: {
  channelId: string;
  onCreated: (webhook: CreatedWebhook) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [error, setError] = useState<string>();
  const { t } = useTranslation();
  const create = useMutation({
    mutationFn: () => api.webhooks.create(channelId, { name }),
    onSuccess: (webhook) => {
      setName('');
      onCreated(webhook);
      void queryClient.invalidateQueries({ queryKey: keys.webhooks(channelId) });
    },
    onError: (err) => setError(errorMessage(err)),
  });

  function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const parsed = createWebhookSchema.safeParse({ name });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message);
    create.mutate();
  }

  return (
    <form onSubmit={onSubmit} className="mb-4 flex flex-col gap-2">
      <div className="flex gap-2">
        <Input
          aria-label={t('Nombre del webhook')}
          placeholder={t('Nombre, p. ej. GitHub o Alertas')}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError(undefined);
          }}
        />
        <Button type="submit" loading={create.isPending}>
          {t('Crear')}
        </Button>
      </div>
      {error && <p className="text-xs text-danger">{error}</p>}
    </form>
  );
}

function RevealedUrl({ webhook, onDismiss }: { webhook: CreatedWebhook; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();
  const example = `curl -X POST "${webhook.url}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"content": "${t('Hola desde {{name}} 👋', { name: webhook.name })}"}'`;
  return (
    <div className="mb-4 rounded-xl border border-accent/30 bg-accent-soft/50 p-3">
      <p className="text-sm font-medium">{t('URL de «{{name}}»', { name: webhook.name })}</p>
      <p className="mb-2 text-xs text-muted">
        {t(
          'Cópiala ahora: por seguridad no se volverá a mostrar. Quien la tenga puede publicar en el canal.',
        )}
      </p>
      <div className="flex gap-2">
        <Input
          readOnly
          value={webhook.url}
          onFocus={(e) => e.target.select()}
          className="h-8 font-mono text-[11px]"
        />
        <IconButton
          label={t('Copiar URL')}
          onClick={() => {
            void navigator.clipboard.writeText(webhook.url).then(() => setCopied(true));
          }}
        >
          {copied ? <Check className="text-success" /> : <Copy />}
        </IconButton>
      </div>
      <pre className="scrollbar-thin mt-3 overflow-x-auto rounded-lg bg-ink p-3 text-[11px] leading-relaxed text-ink-fg">
        {example}
      </pre>
      <Button size="sm" variant="ghost" className="mt-2" onClick={onDismiss}>
        {t('Hecho')}
      </Button>
    </div>
  );
}
